/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useRouteLoaderData, useLoaderData, useParams } from 'react-router-dom';
import { useSubscription } from '@apollo/client';
import { Event, Registration, CreateRegistrationInput, RegistrationStatusEnum } from './API';
import AttendeeList from './AttendeeList';
import ScanTicket from './ScanTicket';
import EventQr from './EventQr';
import FileUpload from "./FileUpload";
import NavBar from './NavBar';
import AddUser from './AddUserModal';
import { Button, Input, Select } from '@uclaea/react-component-library';
import { ON_CREATE_REGISTRATION } from './graphql/subscriptions/onCreateRegistration';
import { ON_UPDATE_REGISTRATION } from './graphql/subscriptions/onUpdateRegistration';
import { createRegistrationRecord } from './mutations/processCreateRegistration';
import ZplPrinter, { Attendee } from './components/ZplPrinter';
import './css/Event.css';
import Switch from 'react-switch';

const guestCountOptions = Array.from({ length: 12 }, (_, index) => {
  const guestCount = index + 1;

  return {
    label: guestCount.toString(),
    id: `guest-count-${guestCount}`,
    value: guestCount.toString(),
  };
});

const EventItem: React.FC = () => {
  // Fetch data from loaders
  const events = useRouteLoaderData("events") as Event[];
  const [registrations, setRegistrations] = useState<Registration[]>(useLoaderData() as Registration[]);
  const { eventId } = useParams();

  // State management
  const [scanTicket, setScanTicket] = useState(false);
  const [viewQr, setViewQr] = useState(false);
  const [showSelfRegistration, setShowSelfRegistration] = useState(false); // TODO set to false in production
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddGuestModal, setShowAddGuestModal] = useState(false);
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestCount, setGuestCount] = useState('1');
  const [addGuestLoading, setAddGuestLoading] = useState(false);
  const [guestFeedback, setGuestFeedback] = useState<string | null>(null);
  const [userFeedback, setUserFeedback] = useState<string | null>(null);
  const [zplFeedback, setZplFeedback] = useState<string | null>(null);
  const [printingEnabled, setPrintingEnabled] = useState(false);
  const [printerReady, setPrinterReady] = useState(false);
  const [pendingPrintAttendee, setPendingPrintAttendee] = useState<Attendee | null>(null);

  // Get current event
  const event = events.find(e => e.eventId === eventId);
  if (!eventId || !event) return <div>Not Found</div>;

  const { eventName, location, date, description, eventRole } = event;

  // Subscriptions
  const { data: newRegistrationData, error: newRegistrationError } = useSubscription(ON_CREATE_REGISTRATION, {
    variables: { eventId },
  });

  const { data: updatedRegistrationData, error: updatedRegistrationError } = useSubscription(ON_UPDATE_REGISTRATION, {
    variables: { eventId },
  });

  // Handle new registrations
  useEffect(() => {
    if (newRegistrationError) {
      console.error('Error with new registration subscription:', newRegistrationError);
    }

    if (newRegistrationData) {
      const newRegistration = newRegistrationData?.onCreateRegistration as Registration;
      setRegistrations(prev => [
        ...prev.filter(r => r.registrationId !== newRegistration.registrationId),
        newRegistration
      ]);
    }
  }, [newRegistrationData, newRegistrationError]);

  // Handle updated registrations and trigger print if enabled
  useEffect(() => {
    if (updatedRegistrationError) {
      console.error('Error with updated registration subscription:', updatedRegistrationError);
    }

    if (updatedRegistrationData) {
      const updatedRegistration = updatedRegistrationData?.onUpdateRegistration as Registration;
      setRegistrations(prev =>
        prev.map(reg => reg.registrationId === updatedRegistration.registrationId ? updatedRegistration : reg)
      );
      // Auto-print if enabled and status is CHECKED_IN
      if (printingEnabled && updatedRegistration.registrationStatus === 'CHECKED_IN') {
        setPendingPrintAttendee(updatedRegistration);
      }
    }
  }, [updatedRegistrationData, updatedRegistrationError, printingEnabled]);

  // Event Handlers
  const startScanningTicket = () => setScanTicket(!scanTicket);
  const handleAddUserClick = () => setShowAddUserModal(true);
  const handleCloseAddUserModal = () => setShowAddUserModal(false);
  const readableDate = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handleAddUserSuccess = () => {
    setUserFeedback('User added to event team successfully!');
    setTimeout(() => setUserFeedback(null), 4000);
    setShowAddUserModal(false);
  };
  const handleAddUserError = () => {
    setUserFeedback('Failed to add user to event team.');
    setTimeout(() => setUserFeedback(null), 4000);
  };

  const handleAddGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddGuestLoading(true);
    setGuestFeedback(null);
    try {
      const input: CreateRegistrationInput = {
        eventId: eventId!,
        email: guestEmail,
        firstName: guestFirstName,
        lastName: guestLastName,
        registrationStatus: RegistrationStatusEnum.REGISTERED,
        live: true,
        guests: Number(guestCount),
        generateQRAndTicket: false,
      };
      const response = await createRegistrationRecord(input);
      if (response && response.registrationId) {
        // Do NOT update registrations here! Let the subscription handle it.
        setShowAddGuestModal(false);
        setGuestFirstName('');
        setGuestLastName('');
        setGuestEmail('');
        setGuestCount('1');
        setGuestFeedback('Guest added successfully!');
        setTimeout(() => setGuestFeedback(null), 7000);
      } else {
        console.error('Add Guest: No registrationId in response', response);
        setGuestFeedback('Failed to add guest.');
      }
    } catch (err) {
      setGuestFeedback('Error adding guest.');
      console.error('Add Guest: Exception thrown', err);
    } finally {
      setAddGuestLoading(false);
    }
  };

  // Handle printing toggle
  const handleTogglePrinting = () => {
    if (!printingEnabled) {
      const confirmed = window.confirm(
        'Zebra Browser Print must be initialized and the default printer must be configured.'
      );
      if (confirmed) {
        setPrintingEnabled(true);
        setPendingPrintAttendee({}); // Print test label
      }
    } else {
      setPrintingEnabled(false);
    }
  };

  // ZplPrinter print result feedback
  const handlePrinterReady = (ready: boolean) => setPrinterReady(ready);
  const handlePrintResult = (result: { success: boolean; message: string }) => setZplFeedback(result.message);

  return scanTicket ? (
    <ScanTicket
      eventId={eventId}
      handler={() => null} // Adjust based on scan handling requirements
      goBack={() => setScanTicket(false)}
      attendees={registrations}
      setAttendees={setRegistrations}
    />
  ) : viewQr ? (
    <EventQr event={event} setViewQr={setViewQr} />
  ) : (
    <div className="event-details">
      <NavBar eventId={eventId} eventName={eventName} />
      <h1 className="event-details-header">
        Event Details for <br />
        <b>{eventName}</b>
      </h1>
      <div className="event-meta">
        <span className="event-meta-location">{location}</span>
        <span className="event-meta-date">{readableDate}</span>
      </div>
      <p className="event-description">{description}</p>

      {/* Action Buttons and Feedback */}
      <div className="event-actions-group">
        {/* Row 1: Scan Tickets full width */}
        <div className="event-actions-row event-actions-row--full">
          <Button
            variant="primary"
            label="Scan Tickets"
            className="event-button event-button--full"
            onClick={startScanningTicket}
            disabled={!registrations.length}
          />
        </div>
        {/* Row 2: Add Users and Add Guest split evenly */}
        <div className="event-actions-row event-actions-row--split">
          {['OWNER', 'COOWNER'].includes(eventRole) && (
            <Button
              variant="primary"
              label="Add Users To Event Team"
              className="event-button event-button--half"
              onClick={handleAddUserClick}
            />
          )}
          {['OWNER', 'COOWNER', 'TEAM'].includes(eventRole) && (
            <Button
              variant="primary"
              label="Add Guest"
              className="event-button event-button--half"
              onClick={() => setShowAddGuestModal(true)}
            />
          )}
        </div>
        {/* Feedback messages */}
        {userFeedback && <div className={`event-actions-feedback ${userFeedback.includes('success') ? 'feedback--success' : 'feedback--error'}`}>{userFeedback}</div>}
        {guestFeedback && <div className={`event-actions-feedback ${guestFeedback.includes('success') ? 'feedback--success' : 'feedback--error'}`}>{guestFeedback}</div>}
        {/* Row 3: FileUpload centered below */}
        {['OWNER', 'COOWNER'].includes(eventRole) && (
          <>
            <div className="event-actions-row event-actions-row--center">
              <FileUpload eventId={eventId} />
            </div>
            <div className="event-actions-row event-actions-row--center printing-toggle-row">
              <div className="printing-toggle-inner">
                <span className="printing-toggle-label">Enable Printing</span>
                <Switch
                  checked={printingEnabled}
                  onChange={handleTogglePrinting}
                  color="primary"
                />
                {/* <span style={{ marginLeft: 8 }}>{printingEnabled ? 'ON' : 'OFF'}</span> */}
              </div>
            </div>
            {zplFeedback && (
              <div className={`event-actions-feedback ${zplFeedback.includes('error') ? 'feedback--error' : 'feedback--success'}`}>{zplFeedback}</div>
            )}
            {/* ZplPrinter is headless, used for setup and printing */}
            {printingEnabled && (
              <>
                <ZplPrinter
                  attendee={pendingPrintAttendee}
                  eventInfo={event}
                  onPrinterReady={handlePrinterReady}
                  onPrintResult={handlePrintResult}
                />
                <div className='event-actions-row event-actions-row--full'>
                  <p>*Printing Enabled. Do not refresh or navigate away from this page while printing is enabled.</p>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Add Guest Modal */}
      {['OWNER', 'COOWNER', 'TEAM'].includes(eventRole) && (
        <>
          {showAddGuestModal && (
            <div className="modal">
              <div className="modal-content">
                <span className="close" onClick={() => setShowAddGuestModal(false)}>&times;</span>
                <h2>Add Guest Registration</h2>
                <form onSubmit={handleAddGuest}>
                  <Input
                    label="First Name"
                    type="text"
                    placeholder="First Name"
                    value={guestFirstName}
                    onChange={e => setGuestFirstName(e.target.value)}
                    required
                  />
                  <Input
                    label="Last Name"
                    type="text"
                    placeholder="Last Name"
                    value={guestLastName}
                    onChange={e => setGuestLastName(e.target.value)}
                    required
                  />
                  <Input
                    label="Email"
                    type="email"
                    placeholder="Email"
                    value={guestEmail}
                    onChange={e => setGuestEmail(e.target.value)}
                    required
                  />
                  <Select
                    label="Guest Count"
                    value={guestCount}
                    onChange={e => setGuestCount(e.target.value)}
                    options={guestCountOptions}
                  />
                  <br />
                  <Button
                    variant="primary"
                    label={addGuestLoading ? 'Adding...' : 'Add Guest'}
                    type="submit"
                    disabled={addGuestLoading}
                  />
                  {guestFeedback && <div className={`event-actions-feedback ${guestFeedback.includes('success') ? 'feedback--success' : 'feedback--error'}`}>{guestFeedback}</div>}
                </form>
              </div>
            </div>
          )}
        </>
      )}

      <AttendeeList attendees={registrations} setAttendees={setRegistrations} />

      {showAddUserModal && (
        <AddUser eventDetails={event} onClose={handleCloseAddUserModal} onSuccess={handleAddUserSuccess} onError={handleAddUserError} />
      )}
    </div>
  );
};

export default EventItem;
